import { FC, Fragment } from "react";
import { useTranslation } from "next-i18next";
import {
    Heading,
    Flex,
    Box,
    Icon,
    Text,
    Link,
} from "@smile2impress/components/relaunch";
import { useUTSLocaleContext } from "context/utsLocaleContext";

import baseContent from "./content";
import { QuestionsContent, QuestionsProps } from "./Questions.types";
import { Question } from "./components/Question";

export const Questions: FC<QuestionsProps> = ({ content, headingAs }) => {
    const { t } = useTranslation("faq");
    const { locale } = useUTSLocaleContext();
    const contentData =
        content || (baseContent(locale)(t, { locale }) as QuestionsContent);

    const {
        title,
        items,
        contacts,
        large = false,
        extraTitleStyles = {},
    } = contentData;
    const itemsWithoutEmptyQuestionOrAnswer = items.filter(
        ({ question, answer }) => Boolean(question) && Boolean(answer)
    );

    return (
        <Flex
            direction={["column", "column", "row"]}
            gap={[3, 3, "10%"]}
            px={[0, 0, 4]}
        >
            {title && (
                <Flex basis={["100%", "100%", "40%"]} flexDirection="column">
                    <Heading
                        textStyle={["title1"]}
                        color="text.base"
                        as={headingAs}
                        w="100%"
                        {...extraTitleStyles}
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                    {contacts && (
                        <Flex
                            flexDirection="column"
                            gap={1}
                            alignItems="flex-start"
                            mt={2}
                        >
                            {contacts.map(({ icon, label, link }, index) => (
                                <Link
                                    key={`${label}-${index}`}
                                    display="flex"
                                    alignItems="center"
                                    href={link}
                                    target="_blank"
                                >
                                    <Icon
                                        as={icon}
                                        boxSize={"24px"}
                                        mr="12px"
                                    />
                                    <Text
                                        textStyle="body"
                                        dangerouslySetInnerHTML={{
                                            __html: label,
                                        }}
                                    />
                                </Link>
                            ))}
                        </Flex>
                    )}
                </Flex>
            )}
            <Flex
                basis={["100%", "100%", title ? "50%" : "100%"]}
                direction="column"
            >
                {itemsWithoutEmptyQuestionOrAnswer.map((item, index) => (
                    <Fragment key={`${item.answer}-${index}`}>
                        <Question {...item} large={large} />
                        {index <
                            itemsWithoutEmptyQuestionOrAnswer.length - 1 && (
                            <Box
                                borderBottom="1px solid"
                                opacity="0.1"
                                borderColor="base.gunmetal"
                                _dark={{
                                    borderColor: "bg.baseInverse",
                                }}
                            />
                        )}
                    </Fragment>
                ))}
            </Flex>
        </Flex>
    );
};
